import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight, User, Key, Search, HelpCircle, MessageSquare, Settings, ShoppingCart, LogOut, FileText, Lock, Bell } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useAuth } from "@/hooks/useAuth";
import { userHasVerifiedTokens } from "@/utils/blogUtils";
import { StickerPacksDrawer } from "@/components/stickers/StickerPacksDrawer";
import { AboutOwnerProfile } from "@/components/about/AboutOwnerProfile";
import { getDemoProfile } from "@/utils/demoProfile";
const logoUrl = 'https://res.cloudinary.com/deqzsmrxq/image/upload/v1760986359/android-chrome-192x192_ffsgg9.png';
import MobileBottomNav from "./MobileBottomNav";

type NavItem = {
  icon: React.ElementType;
  label: string;
  path: string;
  requiresToken?: boolean;
};

const navItems: NavItem[] = [{
  icon: User,
  label: "Profile",
  path: "/profile"
}, {
  icon: Key,
  label: "Metefy Token",
  path: "/token"
}, {
  icon: FileText,
  label: "Blogs",
  path: "/my-blogs",
  requiresToken: true
}, {
  icon: HelpCircle,
  label: "Lost Items",
  path: "/lost"
}, {
  icon: Search,
  label: "Found Items",
  path: "/found"
}, {
  icon: MessageSquare,
  label: "Messaging",
  path: "/messaging"
}, {
  icon: Bell,
  label: "Reminders",
  path: "/reminders"
}, {
  icon: Settings,
  label: "Settings",
  path: "/settings"
}];

export default function DashboardSidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const [hasToken, setHasToken] = useState<boolean | null>(null);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isStickerDrawerOpen, setIsStickerDrawerOpen] = useState(false);
  const [isDemoProfileOpen, setIsDemoProfileOpen] = useState(false);
  const isMobile = useIsMobile();
  const location = useLocation();
  const { user, signOut } = useAuth();

  const openDemoProfile = (profile: any) => {
    setIsDemoProfileOpen(true);
  };

  // Set collapsed to true by default on mobile
  useEffect(() => {
    setCollapsed(isMobile);
  }, [isMobile]);

  // Handle scroll effect for mobile header - always enabled
  useEffect(() => {
    if (!isMobile) return;
    
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Hide on scroll down, show on scroll up
      if (currentScrollY < 10) {
        setIsHeaderVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsHeaderVisible(false);
      } else if (currentScrollY < lastScrollY) {
        setIsHeaderVisible(true);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isMobile, lastScrollY]);

  // Check if user has verified tokens
  useEffect(() => {
    const checkToken = async () => {
      if (!user) {
        setHasToken(false);
        return;
      }
      try {
        const hasTokens = await userHasVerifiedTokens(user.id);
        setHasToken(hasTokens);
      } catch (error) {
        console.error('Error checking tokens:', error);
        setHasToken(false);
      }
    };
    checkToken();
  }, [user]);

  // Mobile header and bottom navigation
  if (isMobile) {
    return (
      <>
        <div className={`fixed top-0 left-0 right-0 z-50 flex items-center h-16 px-4 bg-background transition-transform duration-300 ${
          !isHeaderVisible ? '-translate-y-full' : 'translate-y-0'
        }`}>
          <Link to="/" className="flex items-center gap-2">
            <div 
              className="w-8 h-8"
              style={{
                maskImage: 'url(https://res.cloudinary.com/deqzsmrxq/image/upload/v1760986359/android-chrome-192x192_ffsgg9.png)',
                maskSize: 'contain',
                maskRepeat: 'no-repeat',
                maskPosition: 'center',
                backgroundColor: 'hsl(var(--primary))',
                WebkitMaskImage: 'url(https://res.cloudinary.com/deqzsmrxq/image/upload/v1760986359/android-chrome-192x192_ffsgg9.png)',
                WebkitMaskSize: 'contain',
                WebkitMaskRepeat: 'no-repeat',
                WebkitMaskPosition: 'center',
              }}
              role="img"
              aria-label="Metefy Logo"
            />
            <span className="font-semibold text-lg text-foreground">Metefy</span>
          </Link>
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={signOut}
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
              aria-label="Logout"
            >
              <LogOut className="h-4 w-4" />
            </Button>
            <Button
              onClick={() => setIsStickerDrawerOpen(true)}
              className="bg-primary hover:bg-primary/90 text-primary-foreground whitespace-nowrap text-sm h-8 px-3 rounded-md"
            >
              <ShoppingCart className="mr-2 h-4 w-4" />
              Stickers
            </Button>
          </div>
        </div>
        <MobileBottomNav />

        {/* Mobile Sticker Packs Drawer and Demo Profile */}
        <StickerPacksDrawer
          isOpen={isStickerDrawerOpen}
          setIsOpen={setIsStickerDrawerOpen}
          openDemoProfile={openDemoProfile}
        />
        <AboutOwnerProfile
          isOpen={isDemoProfileOpen}
          setIsOpen={setIsDemoProfileOpen}
          profile={getDemoProfile() as any}
          copyTweetTemplate={() => {}}
          isDemo={true}
        />
      </>
    );
  }

  // Desktop sidebar
  return (
    <aside className={cn(
      "h-screen bg-card relative flex flex-col border-r border-border transition-all duration-300 ease-in-out", 
      collapsed ? "w-[70px]" : "w-[250px]"
    )}>
      {/* Desktop Sidebar Header */}
      <div className="flex items-center justify-between h-16 px-4">
        {/* Logo group */}
        <div className="flex items-center gap-2">
          <Link to="/" className={cn("flex items-center gap-2", collapsed && "hidden")}>
            <div 
              className="w-8 h-8"
              style={{
                maskImage: 'url(https://res.cloudinary.com/deqzsmrxq/image/upload/v1760986359/android-chrome-192x192_ffsgg9.png)',
                maskSize: 'contain',
                maskRepeat: 'no-repeat',
                maskPosition: 'center',
                backgroundColor: 'hsl(var(--primary))',
                WebkitMaskImage: 'url(https://res.cloudinary.com/deqzsmrxq/image/upload/v1760986359/android-chrome-192x192_ffsgg9.png)',
                WebkitMaskSize: 'contain',
                WebkitMaskRepeat: 'no-repeat',
                WebkitMaskPosition: 'center',
              }}
              role="img"
              aria-label="Metefy Logo"
            />
            <span className="font-semibold text-lg text-foreground">Metefy</span>
          </Link>
          <Link to="/" className={cn("hidden", collapsed && "flex mx-auto")}>
            <div 
              className="w-8 h-8"
              style={{
                maskImage: 'url(https://res.cloudinary.com/deqzsmrxq/image/upload/v1760986359/android-chrome-192x192_ffsgg9.png)',
                maskSize: 'contain',
                maskRepeat: 'no-repeat',
                maskPosition: 'center',
                backgroundColor: 'hsl(var(--primary))',
                WebkitMaskImage: 'url(https://res.cloudinary.com/deqzsmrxq/image/upload/v1760986359/android-chrome-192x192_ffsgg9.png)',
                WebkitMaskSize: 'contain',
                WebkitMaskRepeat: 'no-repeat',
                WebkitMaskPosition: 'center',
              }}
              role="img"
              aria-label="Metefy Logo"
            />
          </Link>
        </div>
        {/* Collapse Button only */}
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => setCollapsed(!collapsed)} 
          className="text-foreground/70 hover:bg-muted transition-colors duration-200"
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </Button>
      </div>

      {/* Nav items */}
      <div className={cn(
        "flex flex-col flex-grow py-4 px-2 space-y-1",
        !collapsed && "overflow-y-auto"
      )}>
        {navItems.map((item, index) => {
          const isLocked = item.requiresToken && hasToken === false;
          const isActive = location.pathname === item.path;
          
          return (
            <Link 
              key={index} 
              to={isLocked ? "/token" : item.path}
              onClick={(e) => {
                if (isLocked) {
                  e.preventDefault();
                }
              }}
              className={cn(
                "flex items-center gap-3 px-3 py-2 rounded-md transition-all duration-200 border-l-4 relative",
                isActive && !isLocked
                  ? "bg-primary/10 border-primary text-primary font-semibold"
                  : isLocked
                  ? "border-transparent text-foreground/50 cursor-not-allowed opacity-60"
                  : "border-transparent text-foreground/80 hover:bg-primary/10 hover:border-primary/50 hover:text-foreground",
                collapsed && "justify-center px-2"
              )}
              title={isLocked ? "Verify a Metefy token to access blogs" : item.label}
            >
              <div className="flex items-center justify-center w-5 h-5 relative">
                <item.icon size={20} className={cn(
                  isActive && !isLocked ? "text-primary" : isLocked ? "text-foreground/50" : "text-foreground/80"
                )} />
                {isLocked && (
                  <Lock size={12} className="absolute -top-1 -right-1 text-foreground/50" />
                )}
              </div>
              <span className={cn(
                "transition-all duration-300",
                collapsed ? "w-0 opacity-0 absolute left-full" : "w-auto opacity-100"
              )}>
                {item.label}
              </span>
            </Link>
          );
        })}
      </div>

      {/* Stickers section above logout */}
      <div className="border-t border-border p-4">
        {!collapsed ? (
          <Button
            onClick={() => setIsStickerDrawerOpen(true)}
            className="bg-primary hover:bg-primary/90 text-primary-foreground whitespace-nowrap text-sm sm:text-base h-8 sm:h-9 px-3 sm:px-4 rounded-md w-full"
          >
            <ShoppingCart className="mr-2 h-4 w-4" />
            Stickers
          </Button>
        ) : (
          <button
            onClick={() => setIsStickerDrawerOpen(true)}
            className="flex items-center justify-center w-8 h-8 text-foreground/70 hover:bg-muted transition-colors duration-200 rounded-md"
          >
            <ShoppingCart size={20} />
          </button>
        )}
      </div>

      {/* Logout and version */}
      <div className="mt-auto p-4 flex items-center justify-between border-t border-border">
        <div className={cn("flex flex-col w-full mb-2", collapsed && "hidden")}>
          <Button
            variant="ghost"
            size="sm"
            onClick={signOut}
            className="w-full justify-start px-2 text-destructive hover:text-destructive hover:bg-destructive/10 rounded-md"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
        <div className={cn("text-xs text-muted-foreground", collapsed && "hidden")}>
          <p>Metefy v1.0.0</p>
        </div>
      </div>

      <StickerPacksDrawer isOpen={isStickerDrawerOpen} setIsOpen={setIsStickerDrawerOpen} openDemoProfile={openDemoProfile} />
      <AboutOwnerProfile
        isOpen={isDemoProfileOpen}
        setIsOpen={setIsDemoProfileOpen}
        profile={getDemoProfile() as any}
        copyTweetTemplate={() => {}}
        isDemo={true}
      />
    </aside>
  );
}
